/**
 * What an enquiry is, and what makes one valid.
 *
 * Kept apart from both the form and the delivery so there is exactly one
 * definition of "a complete enquiry" — the client can use `FOCUS_AREAS` to
 * render the checkboxes and the server can use `parseEnquiry` to decide
 * whether to trust what came back, without either owning the rules.
 */

/**
 * The four things a review looks at, matching the specimen report in the
 * hero. A visitor who read the case file should find the same four words
 * here — the form is the other half of that illustration.
 */
export const FOCUS_AREAS = [
  "Security posture",
  "Search visibility",
  "Code quality",
  "Checkout path",
  "Something else",
] as const;

export type FocusArea = (typeof FOCUS_AREAS)[number];

export type Enquiry = {
  name: string;
  email: string;
  company: string;
  focus: FocusArea[];
  message: string;
};

export type FieldErrors = Partial<Record<keyof Enquiry, string>>;

/**
 * Deliberately permissive.
 *
 * The only thing worth checking on the client's behalf is that the address
 * could plausibly be routed — one @, something either side, a dot in the
 * domain. Every stricter regex on the internet rejects somebody's real
 * address, and the actual test of an email address is whether mail to it
 * arrives.
 */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Exported because the form puts them on the inputs as `maxlength` and
 * `minlength`, which is what validates the no-JS path — the browser enforces
 * them before the POST ever leaves. Two copies of these numbers would drift,
 * and the direction they drift is a form the browser accepts and the server
 * rejects, with no way to say why.
 */
export const MAX = { name: 120, email: 200, company: 200, message: 4000 };
export const MIN_MESSAGE = 10;

export type ParseResult =
  | { ok: true; enquiry: Enquiry }
  | { ok: false; fieldErrors: FieldErrors };

/**
 * Whatever was typed, before anybody judges it.
 *
 * Separate from validation because the draft is needed on the failure paths
 * too: a form that clears itself when the server rejects it has taken away
 * the thing the visitor spent effort on, and they will not type it twice.
 * `Enquiry` and this share a shape, but only one of them is trustworthy.
 */
export function readDraft(form: FormData): Enquiry {
  const read = (key: string) => {
    const value = form.get(key);
    return typeof value === "string" ? value.trim() : "";
  };

  return {
    name: read("name"),
    email: read("email"),
    company: read("company"),
    message: read("message"),
    focus: form
      .getAll("focus")
      .filter((value): value is string => typeof value === "string")
      .filter((value): value is FocusArea =>
        (FOCUS_AREAS as readonly string[]).includes(value),
      ),
  };
}

export function parseEnquiry(form: FormData): ParseResult {
  const { name, email, company, message, focus } = readDraft(form);

  const fieldErrors: FieldErrors = {};

  if (name === "") fieldErrors.name = "Tell us who you are.";
  else if (name.length > MAX.name) fieldErrors.name = "That is too long.";

  if (email === "") fieldErrors.email = "We need somewhere to reply.";
  else if (email.length > MAX.email) fieldErrors.email = "That is too long.";
  else if (!EMAIL.test(email)) fieldErrors.email = "That does not look right.";

  if (company.length > MAX.company) fieldErrors.company = "That is too long.";

  if (message === "") fieldErrors.message = "Tell us what to look at.";
  else if (message.length < MIN_MESSAGE) {
    fieldErrors.message = "A sentence or two would help.";
  } else if (message.length > MAX.message) {
    fieldErrors.message = "That is longer than we can accept.";
  }

  if (Object.keys(fieldErrors).length > 0) return { ok: false, fieldErrors };

  return { ok: true, enquiry: { name, email, company, focus, message } };
}

/**
 * What the /api/enquiry endpoint answers when asked for JSON.
 *
 * Shared so the Worker cannot invent a shape the form does not handle. The
 * no-JS path never sees this — it gets a redirect instead.
 */
export type EnquiryResponse =
  | { status: "sent" }
  | { status: "invalid"; fieldErrors: FieldErrors }
  | { status: "error"; message: string };
